import * as XLSX from 'xlsx';

type HeaderConfig = {
  name: string;
  sortingAllowed: boolean;
  type: string;
  id?: number;
};

type FilterState = {
  application?: string;
  modelName?: string;
  cloudProvider?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
};

type ExportInput = {
  filters?: FilterState;
  metricsData?: Array<Record<string, any>>;
  charts?: Record<string, any>;
  tableHeaders?: HeaderConfig[];
  tableRows?: Array<Record<string, any>>;
};

const invalidSheetChars = /[\\/?*\[\]:]/g;

const normalizeValue = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  return value;
};

const unwrapChart = (chart: any) => chart?.source ?? chart;

const getAxisLabels = (chart: any) => {
  const source = unwrapChart(chart);
  const xAxis = source?.xAxis;

  if (Array.isArray(xAxis)) return xAxis;
  if (Array.isArray(xAxis?.categories)) return xAxis.categories;
  if (Array.isArray(xAxis?.regions)) return xAxis.regions;
  if (Array.isArray(xAxis?.env)) return xAxis.env;
  if (Array.isArray(xAxis?.providers)) return xAxis.providers;
  if (Array.isArray(source?.categories)) return source.categories;
  if (Array.isArray(source?.labels)) return source.labels;
  return [];
};

const getSeriesList = (chart: any) => {
  const source = unwrapChart(chart);

  if (Array.isArray(source?.series)) return source.series;
  if (Array.isArray(source?.yAxis) && source?.yAxis?.length && typeof source?.yAxis?.[0] === 'object') {
    return source.yAxis;
  }
  if (Array.isArray(source?.leftYaxis) || Array.isArray(source?.rightYaxis)) {
    return [
      Array.isArray(source?.leftYaxis)
        ? { name: source?.axisName?.leftYaxis || 'Left Series', data: source.leftYaxis }
        : null,
      Array.isArray(source?.rightYaxis)
        ? { name: source?.axisName?.rightYaxis || 'Right Series', data: source.rightYaxis }
        : null
    ].filter(Boolean);
  }

  return [];
};

const toRows = (columns: string[], items: Array<Record<string, any>>) =>
  items.map((item) =>
    columns.reduce(
      (acc, column) => {
        acc[column] = normalizeValue(item?.[column]);
        return acc;
      },
      {} as Record<string, any>
    )
  );

const addSheet = (workbook: XLSX.WorkBook, sheetName: string, rows: Array<Record<string, any>>) => {
  if (!rows?.length) return;
  const safeName = sheetName.replace(invalidSheetChars, ' ').slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), safeName);
};

const sanitizeFilenamePart = (value: string) =>
  value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_');

const sheetRowsFromChart = (chart: any) => {
  const source = unwrapChart(chart);
  if (!source) return [];

  if (Array.isArray(source?.data) && source.data.length) {
    const total = source.data.reduce((sum: number, item: any) => sum + Number(item?.value || 0), 0);
    return source.data.map((item: any) => ({
      Category: normalizeValue(item?.name),
      Value: normalizeValue(item?.value),
      Percentage: total ? `${((Number(item?.value || 0) / total) * 100).toFixed(2)}%` : '0%'
    }));
  }

  const labels = getAxisLabels(source);
  if (!labels.length) return [];

  const seriesList = getSeriesList(source);

  if (seriesList.length === 1 && Array.isArray(seriesList[0]?.data)) {
    return labels.map((label: any, index: number) => ({
      Category: normalizeValue(label),
      [seriesList[0]?.name || 'Value']: normalizeValue(seriesList[0]?.data?.[index])
    }));
  }

  if (seriesList.length > 1) {
    return labels.map((label: any, index: number) => {
      const row: Record<string, any> = { Category: normalizeValue(label) };
      seriesList.forEach((series: any, seriesIndex: number) => {
        row[series?.name || `Series ${seriesIndex + 1}`] = normalizeValue(series?.data?.[index]);
      });
      return row;
    });
  }

  if (Array.isArray(source?.yAxis)) {
    return labels.map((label: any, index: number) => ({
      Category: normalizeValue(label),
      Value: normalizeValue(source?.yAxis?.[index])
    }));
  }

  return [];
};

const filtersRows = (filters?: FilterState) => [
  { Field: 'Application', Value: normalizeValue(filters?.application) },
  { Field: 'Model', Value: normalizeValue(filters?.modelName) },
  { Field: 'Cloud Provider', Value: normalizeValue(filters?.cloudProvider) },
  { Field: 'Date From', Value: normalizeValue(filters?.dateFrom) },
  { Field: 'Date To', Value: normalizeValue(filters?.dateTo) },
  { Field: 'Search', Value: normalizeValue(filters?.search) }
];

const downloadWorkbook = (workbook: XLSX.WorkBook, fileName: string) => {
  const data = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportCostAnalyticsReport = ({
  filters,
  metricsData = [],
  charts = {},
  tableHeaders = [],
  tableRows = []
}: ExportInput) => {
  const workbook = XLSX.utils.book_new();

  addSheet(workbook, 'Filters', filtersRows(filters));

  if (metricsData.length) {
    addSheet(
      workbook,
      'KPI Cards',
      metricsData.map((item) => ({
        Title: normalizeValue(item?.title),
        Value: normalizeValue(item?.value),
        Subtitle: normalizeValue(item?.subtitle)
      }))
    );
  }

  addSheet(workbook, 'Cost Over Time', sheetRowsFromChart(charts?.costOverTime));
  addSheet(workbook, 'Cost by ICC', sheetRowsFromChart(charts?.icc));
  addSheet(workbook, 'Cost by CC', sheetRowsFromChart(charts?.cc));
  addSheet(workbook, 'App Cost Distribution', sheetRowsFromChart(charts?.appCostDistribution));
  addSheet(workbook, 'Cost by Model', sheetRowsFromChart(charts?.costByModel));
  addSheet(workbook, 'Cost by Requester', sheetRowsFromChart(charts?.costByRequester));
  addSheet(workbook, 'Tokens by Application', sheetRowsFromChart(charts?.tokenByApplication));
  addSheet(workbook, 'Tokens by Model', sheetRowsFromChart(charts?.tokenByModel));
  addSheet(workbook, 'Cost by Environment', sheetRowsFromChart(charts?.costByEnvironment));

  if (tableRows.length) {
    const orderedColumns = tableHeaders.length
      ? tableHeaders.map((header) => header.type)
      : Object.keys(tableRows[0] || {});
    const mappedRows = toRows(orderedColumns, tableRows).map((row) => {
      const result: Record<string, any> = {};
      orderedColumns.forEach((column) => {
        result[column] = normalizeValue(row[column]);
      });
      return result;
    });
    addSheet(workbook, 'Prompt Cost Summary', mappedRows);
  }

  const fileName = [
    'cost_analytics',
    filters?.application ? sanitizeFilenamePart(filters.application) : 'all_apps',
    filters?.modelName ? sanitizeFilenamePart(filters.modelName) : 'all_models',
    filters?.dateFrom ? sanitizeFilenamePart(filters.dateFrom) : 'from',
    filters?.dateTo ? sanitizeFilenamePart(filters.dateTo) : 'to'
  ].join('_');

  downloadWorkbook(workbook, `${fileName}.xlsx`);
};
